"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Camera, Search, Wrench, ShieldAlert, PhoneCall, ArrowRight, CircleCheck as CheckCircle2, Activity, Layers, Clock } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const detectionSteps = [{
  step: "01",
  title: "High-Definition Video Inspection",
  description: "We insert specialized fiber-optic waterproof cameras through cleanouts to visually pinpoint cracks, blockages, root intrusion, and pipe collapse without digging.",
  feature: "Zero surface disruption"
}, {
  step: "02",
  title: "Digital Sonar Line Tracing",
  description: "Transmitters on the camera head emit precision locator frequencies, allowing our technicians to mark exact pipe depth and path directly on the lawn or slab.",
  feature: "Accurate to the exact inch"
}, {
  step: "03",
  title: "Hydrostatic Flow & Pressure Testing",
  description: "Comprehensive static pressure tests verify the integrity of the underground drainage system, identifying hidden hairline leaks and joint separations.",
  feature: "Certified diagnostic report"
}];
const repairMethods = [{
  id: "trenchless-lining",
  title: "Cured-In-Place Pipe (CIPP) Lining",
  category: "No-Dig Repair",
  description: "An epoxy-saturated liner is pulled into the damaged sewer lateral and cured with steam or UV light, forming a brand-new, joint-free structural pipe inside the old one.",
  lifespan: "50+ Year Design Life",
  pros: "Preserves driveways, patios, and landscaping with minimal ground access points.",
  bestFor: "Cracked pipes, minor root intrusion, and aging cast iron or clay lines."
}, {
  id: "pipe-bursting",
  title: "Hydraulic Pipe Bursting",
  category: "Trenchless Replacement",
  description: "A hardened conical bursting head fragments the deteriorated pipe while immediately pulling a brand-new seamless high-density polyethylene (HDPE) pipe into place.",
  lifespan: "100+ Year Durability",
  pros: "Can upsize pipe diameter to increase wastewater capacity without full-length trenches.",
  bestFor: "Severely collapsed, crushed, or heavily root-choked sewer lines."
}, {
  id: "conventional-excavation",
  title: "Targeted Open-Trench Excavation",
  category: "Traditional Replacement",
  description: "When utility congestion or total structural collapse prevents trenchless options, our precision excavation team replaces compromised sections quickly and safely.",
  lifespan: "Code-compliant new PVC/ABS installation",
  pros: "Allows complete realignment of bellies and severe ground shifting issues.",
  bestFor: "Severe back-pitched pipes, massive municipal ties, or total structural failure."
}];
const warningSigns = [{
  title: "Multiple Slow Drains",
  detail: "Toilets, tubs, and sinks backing up simultaneously throughout the home."
}, {
  title: "Gurgling Fixtures",
  detail: "Toilets bubbling or gurgling when washing machines drain or showers run."
}, {
  title: "Soggy Yard Patches",
  detail: "Lush, foul-smelling, or indented patches forming on the lawn or driveway edges."
}, {
  title: "Sewage Odors Indoors",
  detail: "Persistent sewer gas smells around floor drains, basements, or cleanout caps."
}];
export default function SewerOverview() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("trenchless-lining");
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="sewerlinerepairoverview" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 font-semibold text-primary border-primary/20">
            <Activity className="size-3.5 text-accent" />
            Underground Plumbing Diagnostics
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Sewer Line Inspection & Repair in Jonesboro, GA
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            From pinpoint video camera diagnostics to trenchless no-dig restoration, Jonesboro Emergency Plumber delivers fast, permanent solutions for damaged and clogged sewer mains.
          </p>
        </motion.div>

        {/* Section 1: 3-Step Detection Process */}
        <div className="mt-16">
          <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-accent">Pinpoint Detection</p>
              <h3 className="font-default text-2xl font-bold text-foreground">
                How We Locate Your Sewer Issue
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              We never guess beneath your foundation or yard. Modern diagnostic tools pinpoint the exact fault location before any repair starts.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {detectionSteps.map((item, i) => <motion.div key={item.step} data-index={i} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeUp}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="gap-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-2xl font-extrabold text-primary/40">
                        {item.step}
                      </span>
                      {i === 0 && <Camera className="size-5 text-primary" />}
                      {i === 1 && <Search className="size-5 text-primary" />}
                      {i === 2 && <Layers className="size-5 text-primary" />}
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                      <CheckCircle2 className="size-3.5 text-accent" />
                      <span>{item.feature}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Section 2: Repair & Replacement Methods (Tabs) */}
        <div className="mt-20">
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp} className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-accent">Proven Solutions</p>
            <h3 className="font-default text-2xl font-bold text-foreground sm:text-3xl">
              Targeted Sewer Repair Methods
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              We choose the method that solves your problem permanently while protecting your property and budget.
            </p>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-4xl mx-auto">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-muted p-1 border border-border">
                <TabsTrigger value="trenchless-lining" className="text-xs md:text-sm font-medium">
                  Pipe Relining (CIPP)
                </TabsTrigger>
                <TabsTrigger value="pipe-bursting" className="text-xs md:text-sm font-medium">
                  Pipe Bursting
                </TabsTrigger>
                <TabsTrigger value="conventional-excavation" className="text-xs md:text-sm font-medium">
                  Excavation Repair
                </TabsTrigger>
              </TabsList>
            </div>

            {repairMethods.map((method, i) => <TabsContent key={method.id} value={method.id} data-index={i}>
                <Card className="border border-border bg-card shadow-md">
                  <CardHeader className="pb-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Badge variant="secondary" className="font-semibold text-secondary-foreground">
                        {method.category}
                      </Badge>
                      <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Clock className="size-3.5 text-accent" />
                        {method.lifespan}
                      </span>
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-bold text-card-foreground mt-2">
                      {method.title}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base text-muted-foreground">
                      {method.description}
                    </CardDescription>
                  </CardHeader>
                  <Separator className="my-2" />
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                    <div className="rounded-lg bg-background p-4 border border-border">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-foreground mb-1">
                        Key Advantage
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {method.pros}
                      </p>
                    </div>
                    <div className="rounded-lg bg-background p-4 border border-border">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-foreground mb-1">
                        Best Recommended For
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {method.bestFor}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Section 3: Signs of Urgent Sewer Line Distress */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="mt-20 rounded-2xl bg-secondary p-6 md:p-10 border border-border">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 space-y-4">
              <div className="inline-flex items-center gap-2 text-destructive font-semibold text-xs tracking-wider uppercase">
                <ShieldAlert className="size-4" />
                Urgent Warning Signs
              </div>
              <h3 className="font-default text-2xl font-bold text-secondary-foreground sm:text-3xl">
                Do You Need Immediate Sewer Line Attention?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Ignoring mainline warnings can cause catastrophic raw sewage backups inside your home or foundation damage. Our 24/7 Jonesboro technicians are ready to respond.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <a href="tel:4708479076" className="flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call 24/7 Dispatch</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border">
                  <Link to="/contact/" className="flex items-center gap-2">
                    <span>Schedule Inspection</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {warningSigns.map((sign, i) => <div key={sign.title} data-index={i} className="rounded-xl bg-card p-5 border border-border shadow-xs">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-accent/20 p-1.5 text-accent-foreground shrink-0 mt-0.5">
                      <Wrench className="size-4 text-accent" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-card-foreground">
                        {sign.title}
                      </h4>
                      <p className="mt-1 text-xs text-muted-foreground leading-normal">
                        {sign.detail}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}